/**
 * useUnifiedUpload - Hook for the Save-First Upload Pipeline
 * 
 * Supports both single and batch uploads (up to 100 files):
 * - Immediate S3 save (files never lost)
 * - Background processing with status polling
 * - Progress tracking per file and overall
 * - Error handling with detailed status
 */

import { useState, useCallback, useRef, useEffect } from 'react';
// Alias the api helper because this hook also exposes its OWN
// `uploadFiles` (the user-facing entry point at line ~232). Without
// the alias the local declaration shadows the import for the whole
// hook scope, breaking the line-120 batch-upload call to TypeScript
// (TS2554 — fix 2026-04-26 archaeology pass).
import { uploadFiles as apiUploadFiles, apiFetch } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface UploadStatus {
  upload_id: string;
  status: 'uploaded' | 'processing' | 'parsed' | 'failed';
  processing_stage: string | null;
  progress_percent: number;
  result: {
    transactions: Array<{
      date: string;
      description: string;
      amount: number;
      category: string;
    }>;
    extraction_method?: string;
    rows_processed?: number;
  } | null;
  error_message: string | null;
}

export interface FileUploadState {
  file: File;
  upload_id: string | null;
  status: 'pending' | 'uploading' | 'processing' | 'parsed' | 'failed';
  progress: number;
  error: string | null;
  result: UploadStatus['result'] | null;
  transactionsCount?: number;
  retryCount?: number;  // Track retry attempts
}

interface BatchStatusResponse {
  summary: {
    total: number;
    completed: number;
    processing: number;
    failed: number;
    success_rate: string;
  };
  uploads: Array<{
    upload_id: string;
    filename: string;
    status: string;
    processing_stage: string | null;
    progress_percent: number;
    error_message: string | null;
    transactions_count: number | null;
  }>;
}

interface UseUnifiedUploadOptions {
  claimId?: string;
  draftId?: string;
  uploaderType?: 'client' | 'specialist';
  clientName?: string;
  portalToken?: string; // Magic token for portal client uploads (no Clerk JWT available)
  onUploadComplete?: (file: FileUploadState) => void;
  onAllComplete?: (files: FileUploadState[]) => void;
  pollInterval?: number;
  maxPollAttempts?: number;
  useBatchEndpoint?: boolean; // Default true for multiple files
  autoRetryOnFailure?: boolean; // Automatically retry failed files once (default true)
}

export function useUnifiedUpload(options: UseUnifiedUploadOptions = {}) {
  const {
    claimId,
    draftId,
    uploaderType = 'specialist',
    clientName,
    portalToken,
    onUploadComplete,
    onAllComplete,
    pollInterval = 2000,
    maxPollAttempts = 90, // 3 minutes max polling
    useBatchEndpoint = true,
    autoRetryOnFailure = true, // Automatic retry enabled by default
  } = options;

  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [batchUploadIds, setBatchUploadIds] = useState<string[]>([]);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);
  const retriedUploadsRef = useRef<Set<string>>(new Set()); // Track which uploads have been retried
  
  // Use ref to avoid circular dependency in useCallback
  const autoRetryFnRef = useRef<((ids: string[]) => Promise<void>) | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  // Batch upload multiple files at once
  const uploadBatch = useCallback(async (filesToUpload: File[]): Promise<string[]> => {
    const formData = new FormData();
    filesToUpload.forEach(file => formData.append('files', file));
    if (claimId) formData.append('claim_id', claimId);
    if (draftId) formData.append('draft_id', draftId);
    formData.append('uploader_type', uploaderType);
    if (clientName) formData.append('client_name', clientName);
    // Portal clients have no Clerk session — send magic token as alternative auth
    if (portalToken) formData.append('portal_token', portalToken);

    try {
      const result = await apiUploadFiles('/api/v1/upload-savefirst/unified/batch', formData, {
        showErrorToast: false
      });
      
      // Return upload IDs for successful uploads
      return result.uploads
        .filter((u: any) => u.upload_id)
        .map((u: any) => u.upload_id);
    } catch (error) {
      console.error('Batch upload error:', error);
      return [];
    }
  }, [claimId, draftId, uploaderType, clientName]);

  // Poll batch status
  const pollBatchStatus = useCallback(async (uploadIds: string[]): Promise<BatchStatusResponse | null> => {
    if (uploadIds.length === 0) return null;
    
    try {
      const data = await apiFetch(
        `${BACKEND_URL}/api/v1/upload-savefirst/unified/batch/status?upload_ids=${uploadIds.join(',')}`,
        { showErrorToast: false }
      );
      return await data.json();
    } catch {
      return null;
    }
  }, []);

  // Start batch polling
  const startBatchPolling = useCallback((uploadIds: string[], fileMap: Map<string, number>) => {
    pollAttemptsRef.current = 0;

    const poll = async () => {
      pollAttemptsRef.current++;
      const batchStatus = await pollBatchStatus(uploadIds);

      if (batchStatus) {
        // Update file states from batch status
        setFiles(prev => {
          const updated = [...prev];
          batchStatus.uploads.forEach(upload => {
            const fileIndex = fileMap.get(upload.upload_id);
            if (fileIndex !== undefined && updated[fileIndex]) {
              updated[fileIndex] = {
                ...updated[fileIndex],
                status: upload.status as FileUploadState['status'],
                progress: upload.progress_percent,
                error: upload.error_message,
                transactionsCount: upload.transactions_count || 0,
              };
            }
          });
          return updated;
        });

        // Check if all complete (or all that aren't being retried)
        if (batchStatus.summary.processing === 0) {
          // Stop polling
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }

          // Check for failures that haven't been retried yet - trigger auto-retry
          const failedUploadIds = batchStatus.uploads
            .filter(u => u.status === 'failed' && !retriedUploadsRef.current.has(u.upload_id))
            .map(u => u.upload_id);
          
          if (autoRetryOnFailure && failedUploadIds.length > 0) {
            console.log(`🔄 Detected ${failedUploadIds.length} failed files, triggering auto-retry...`);
            // Don't call onAllComplete yet - wait for retry
            if (autoRetryFnRef.current) {
              autoRetryFnRef.current(failedUploadIds);
            }
            return;
          }

          // Call onAllComplete callback
          setFiles(prev => {
            if (onAllComplete) {
              onAllComplete(prev);
            }
            return prev;
          });
          return;
        }
      }

      // Stop after max attempts
      if (pollAttemptsRef.current >= maxPollAttempts) {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        // Mark remaining processing files as timeout
        setFiles(prev => prev.map(f => 
          f.status === 'processing' 
            ? { ...f, status: 'failed' as const, error: 'Processing timeout' }
            : f
        ));
      }
    };

    // Initial poll
    poll();

    // Start interval
    pollTimerRef.current = setInterval(poll, pollInterval);
  }, [pollBatchStatus, pollInterval, maxPollAttempts, onAllComplete, autoRetryOnFailure]);

  // Main upload function
  const uploadFiles = useCallback(async (newFiles: File[]) => {
    if (newFiles.length === 0) return;

    setIsUploading(true);

    // Initialize file states
    const initialStates: FileUploadState[] = newFiles.map(file => ({
      file,
      upload_id: null,
      status: 'uploading',
      progress: 0,
      error: null,
      result: null,
    }));
    setFiles(initialStates);

    // Use batch endpoint
    console.log(`📤 Uploading ${newFiles.length} files via batch endpoint...`);
    
    const uploadIds = await uploadBatch(newFiles);
    
    if (uploadIds.length === 0) {
      // All failed
      setFiles(prev => prev.map(f => ({
        ...f,
        status: 'failed' as const,
        error: 'Upload failed',
      })));
      setIsUploading(false);
      return;
    }

    setBatchUploadIds(uploadIds);

    // Create map of upload_id -> file index
    const fileMap = new Map<string, number>();
    
    // Update states with upload IDs
    setFiles(prev => {
      const updated = prev.map((f, i) => {
        if (i < uploadIds.length) {
          fileMap.set(uploadIds[i], i);
          return {
            ...f,
            upload_id: uploadIds[i],
            status: 'processing' as const,
            progress: 5,
          };
        }
        return {
          ...f,
          status: 'failed' as const,
          error: 'Upload failed',
        };
      });
      return updated;
    });

    // Start batch polling
    startBatchPolling(uploadIds, fileMap);

    setIsUploading(false);
  }, [uploadBatch, startBatchPolling]);

  // Retry a single failed file by calling the reprocess API
  const retryFile = useCallback(async (fileIndex: number) => {
    const file = files[fileIndex];
    if (!file || !file.upload_id) {
      console.error('Cannot retry: no upload_id');
      return;
    }

    // Update status to show retrying
    setFiles(prev => prev.map((f, i) => 
      i === fileIndex 
        ? { ...f, status: 'processing' as const, progress: 5, error: null, retryCount: (f.retryCount || 0) + 1 }
        : f
    ));

    try {
      // Call the reprocess API
      const response = await apiFetch(`${BACKEND_URL}/api/v1/upload-savefirst/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_ids: [file.upload_id],
          limit: 1
        })
      });

      if (response.results && response.results.length > 0) {
        const result = response.results[0];
        if (result.status === 'success') {
          setFiles(prev => prev.map((f, i) => 
            i === fileIndex 
              ? { 
                  ...f, 
                  status: 'parsed' as const, 
                  progress: 100, 
                  transactionsCount: result.new_transactions,
                  error: null 
                }
              : f
          ));
          console.log(`✅ Retry successful for ${file.file.name}: ${result.new_transactions} transactions`);
        } else {
          setFiles(prev => prev.map((f, i) => 
            i === fileIndex 
              ? { ...f, status: 'failed' as const, progress: 0, error: result.error || 'Retry failed' }
              : f
          ));
          console.error(`❌ Retry failed for ${file.file.name}: ${result.error}`);
        }
      }
    } catch (error: any) {
      console.error('Retry error:', error);
      setFiles(prev => prev.map((f, i) => 
        i === fileIndex 
          ? { ...f, status: 'failed' as const, progress: 0, error: error.message || 'Retry failed' }
          : f
      ));
    }
  }, [files]);

  // Automatic retry for failed files (called when polling detects failures)
  const autoRetryFailedFiles = useCallback(async (failedUploadIds: string[]) => {
    if (!autoRetryOnFailure || failedUploadIds.length === 0) return;
    
    // Filter out already retried files
    const toRetry = failedUploadIds.filter(id => !retriedUploadsRef.current.has(id));
    if (toRetry.length === 0) return;

    console.log(`🔄 Auto-retrying ${toRetry.length} failed files...`);
    
    // Mark as being retried
    toRetry.forEach(id => retriedUploadsRef.current.add(id));

    // Update status to show retrying
    setFiles(prev => prev.map(f => 
      f.upload_id && toRetry.includes(f.upload_id)
        ? { ...f, status: 'processing' as const, progress: 5, error: null, retryCount: (f.retryCount || 0) + 1 }
        : f
    ));

    try {
      const response = await apiFetch(`${BACKEND_URL}/api/v1/upload-savefirst/reprocess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_ids: toRetry,
          limit: toRetry.length
        })
      });

      if (response.results) {
        setFiles(prev => prev.map(f => {
          const result = response.results.find((r: any) => r.upload_id === f.upload_id);
          if (!result) return f;
          
          if (result.status === 'success') {
            return { 
              ...f, 
              status: 'parsed' as const, 
              progress: 100, 
              transactionsCount: result.new_transactions,
              error: null 
            };
          } else {
            return { 
              ...f, 
              status: 'failed' as const, 
              progress: 0, 
              error: result.error || 'Retry failed' 
            };
          }
        }));
        
        const successCount = response.results.filter((r: any) => r.status === 'success').length;
        console.log(`✅ Auto-retry complete: ${successCount}/${toRetry.length} succeeded`);
      }
    } catch (error) {
      console.error('Auto-retry error:', error);
    }
  }, [autoRetryOnFailure]);
  
  // Set ref after function is defined
  autoRetryFnRef.current = autoRetryFailedFiles;

  // Clear all files
  const clearFiles = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setFiles([]);
    setBatchUploadIds([]);
    pollAttemptsRef.current = 0;
    retriedUploadsRef.current.clear();
  }, []);

  // Calculated values
  const overallProgress = files.length > 0
    ? Math.round(files.reduce((sum, f) => sum + f.progress, 0) / files.length)
    : 0;

  const completedCount = files.filter(f => f.status === 'parsed').length;
  const failedCount = files.filter(f => f.status === 'failed').length;
  const processingCount = files.filter(f => f.status === 'processing' || f.status === 'uploading').length;

  const allComplete = files.length > 0 && processingCount === 0;
  const anyFailed = failedCount > 0;

  const totalTransactions = files.reduce(
    (sum, f) => sum + (f.transactionsCount || f.result?.transactions?.length || 0), 
    0
  );

  const successfulResults = files
    .filter(f => f.status === 'parsed')
    .map(f => ({ file: f.file, result: f.result!, transactionsCount: f.transactionsCount || 0 }));

  return {
    // State
    files,
    isUploading,
    
    // Actions
    uploadFiles,
    clearFiles,
    retryFile,  // Manual retry for a single file
    
    // Computed
    overallProgress,
    completedCount,
    failedCount,
    processingCount,
    allComplete,
    anyFailed,
    totalTransactions,
    successfulResults,
    
    // Summary
    summary: {
      total: files.length,
      completed: completedCount,
      processing: processingCount,
      failed: failedCount,
      successRate: files.length > 0 
        ? `${Math.round((completedCount / files.length) * 100)}%` 
        : '0%',
    },
  };
}

export default useUnifiedUpload;
